import axios from 'axios';
import {
  IInvoice,
  IProfile,
  IPromoCode,
  IReferralCode,
  IUpdateUserBody,
  IUser,
} from '../interfaces/user';

export interface IUserApi {
  userEndpoint: string;
  referralEndpoint: string;
  profileEndpoint: string;

  fetchUser: (
    wallet: string,
    network: string,
    controller?: AbortController
  ) => Promise<IUser | null>;
  updateUser: (
    wallet: string,
    network: string,
    authToken: string,
    payload: IUpdateUserBody
  ) => Promise<{ status: string } | null>;
  getReferral: (
    network: string,
    authToken: string
  ) => Promise<IReferralCode | null>;
  activateReferral: (
    network: string,
    code: string,
    authToken: string
  ) => Promise<{ status: string } | null>;
  fetchProfile: (authToken?: string) => Promise<IProfile | null>;
  fetchInvoice: (
    email: string,
    vipId: number,
    authToken: string,
    callback?: string
  ) => Promise<IInvoice | null>;
}

export class UserApi implements IUserApi {
  userEndpoint: string;
  referralEndpoint: string;
  profileEndpoint: string;

  constructor(apiGateway: string) {
    if (!apiGateway) {
      apiGateway = 'https://api.kattana.trade';
    }

    this.userEndpoint = `${apiGateway}/user`;
    this.referralEndpoint = `${apiGateway}/referrals`;
    this.profileEndpoint = `${apiGateway}/profile`;
  }

  /**
   * Fetch specific user
   * @param wallet
   * @param network
   * @param controller
   * @returns
   */
  public fetchUser = async (
    wallet: string,
    network: string,
    controller?: AbortController
  ): Promise<IUser | null> => {
    try {
      const config = { signal: controller?.signal };
      const res = await axios.get(
        `${this.userEndpoint}/${network}/${wallet}`,
        config
      );

      return res.data;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  /**
   * Update authentificated user
   * @param wallet
   * @param network
   * @param controller
   * @returns
   */
  public updateUser = async (
    wallet: string,
    network: string,
    authToken: string,
    payload: IUpdateUserBody
  ): Promise<{ status: string } | null> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
      };
      const res = await axios.put(
        `${this.userEndpoint}/${network}/${wallet}`,
        payload,
        config
      );

      return res.data;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  /**
   * Update authentificated user
   * @param wallet
   * @param network
   * @param controller
   * @returns
   */
  public getReferral = async (
    network: string,
    authToken: string
  ): Promise<IReferralCode | null> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
      };
      const res = await axios.get(
        `${this.referralEndpoint}/${network}`,
        config
      );
      return res.data;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  public activateReferral = async (
    network: string,
    code: string,
    authToken: string
  ): Promise<{ status: string } | null> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
      };
      const res = await axios.put(
        `${this.referralEndpoint}/${network}/${code}`,
        {},
        config
      );
      return res.data;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  public fetchProfile = async (
    authToken?: string
  ): Promise<IProfile | null> => {
    try {
      const config = authToken
        ? {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        : undefined;
      const res = await axios(this.profileEndpoint, config);
      return res.data;
    } catch (err) {
      console.warn(err);
      return null;
    }
  };

  public fetchInvoice = async (
    email: string,
    vipId: number,
    authToken: string,
    callback?: string
  ): Promise<IInvoice | null> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
      };
      const res = await axios.post(
        `${this.profileEndpoint}/invoice`,
        { email, vip_id: vipId, callback },
        config
      );
      return res.data;
    } catch (err) {
      console.warn(err);
      return null;
    }
  };

  public checkPromoCode = async (
    code: string,
    authToken: string
  ): Promise<IPromoCode | null> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
      };
      const { data } = await axios.post(
        `${this.profileEndpoint}/code`,
        { code },
        config
      );
      return data;
    } catch (err) {
      console.warn(err);
      return null;
    }
  };
}
